import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dialingProfilesApi, clientsApi } from '../../../api/client';
import Modal, { Button, Badge } from '../../ui/Modal';
import { Label, Input, ErrorBanner } from '../../../auth/AuthShell';
import HelpButton from '../../help/HelpButton';

/**
 * Admin > Outbound Dialing Profiles
 *
 * Profiles define what an agent is allowed to dial out to. Per Fernando
 * 2026-06-18:
 *  - Any admin sees all profiles (no per-client visibility gating)
 *  - Granularity: domestic_us / canada / mexico / intl_other booleans
 *    plus allow / block country code lists
 *  - "Default Block" is system-managed (non-deletable, non-mutable)
 *
 * Enforcement: AstDB (`cx-dial-profile/<ext>`) + dialplan helper
 * `[cx-check-dial-allowed]`. Backend pre-check at `/api/softphone/dial-precheck`
 * gives the browser instant feedback.
 */

const SUMMARY_TAGS = [
  { key: 'allow_domestic_us', short: 'US' },
  { key: 'allow_canada',      short: 'Canada' },
  { key: 'allow_mexico',      short: 'Mexico' },
  { key: 'allow_intl_other',  short: 'Intl' },
];

export default function DialingProfilesPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [resyncMsg, setResyncMsg] = useState(null);

  const { data: profiles = [], isLoading } = useQuery({
    queryKey: ['dialing-profiles'],
    queryFn: dialingProfilesApi.list,
  });
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: clientsApi.list,
  });

  const delMut = useMutation({
    mutationFn: dialingProfilesApi.remove,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dialing-profiles'] }),
  });
  const resyncMut = useMutation({
    mutationFn: dialingProfilesApi.resyncAll,
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['dialing-profiles'] });
      setResyncMsg(`Resynced: ${result.pushed} pushed, ${result.skipped} skipped, ${result.errors} errors (of ${result.total} total)`);
      setTimeout(() => setResyncMsg(null), 6000);
    },
  });

  const onDelete = (p) => {
    if (p.is_default_block) {
      alert('The Default Block profile cannot be deleted.');
      return;
    }
    if (p.assignee_count > 0) {
      alert(`Cannot delete — ${p.assignee_count} agent(s) are still assigned to this profile. Reassign them first.`);
      return;
    }
    if (!confirm(`Delete profile "${p.name}"? This cannot be undone.`)) return;
    delMut.mutate(p.id);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold">Outbound Dialing Profiles</h1>
          <p className="text-sm text-surface-muted dark:text-surface-dark-muted mt-1">
            Per-agent outbound permission profiles. Agents assigned to a profile
            can dial only the destinations the profile allows.
            Agents with <strong>no profile</strong> assigned implicitly get
            "Default Block" (all PSTN denied).
          </p>
          <p className="text-xs text-surface-muted mt-1">
            Final country-zone enforcement lives in Telnyx. This layer is the
            ContinuumCX-side policy filter.
          </p>
        </div>
        <div className="flex gap-2">
          <HelpButton moduleKey="admin_dialing_profiles" />
          <Button variant="secondary"
            disabled={resyncMut.isPending}
            onClick={() => resyncMut.mutate()}>
            {resyncMut.isPending ? 'Resyncing…' : '↻ Resync AstDB'}
          </Button>
          <Button variant="primary" onClick={() => setEditing('new')}>
            + New profile
          </Button>
        </div>
      </div>

      {resyncMsg && (
        <div className="mb-3 px-3 py-2 rounded-thin bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 text-sm">
          {resyncMsg}
        </div>
      )}

      <div className="rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-panel dark:bg-surface-dark-panel overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-subtle dark:bg-surface-dark-subtle text-left">
            <tr className="text-xs uppercase tracking-wider text-surface-muted dark:text-surface-dark-muted">
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Allowed regions</th>
              <th className="px-3 py-2">Explicit allow</th>
              <th className="px-3 py-2">Explicit block</th>
              <th className="px-3 py-2">Client</th>
              <th className="px-3 py-2 text-right">Agents</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-surface-muted">Loading…</td></tr>
            )}
            {!isLoading && profiles.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center text-surface-muted">
                  No profiles yet.
                </td>
              </tr>
            )}
            {profiles.map((p) => (
              <tr key={p.id} className="border-t border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle/40 dark:hover:bg-surface-dark-subtle/40">
                <td className="px-3 py-2">
                  <div className="font-medium flex items-center gap-2">
                    {p.name}
                    {p.is_default_block && <Badge tone="amber">system</Badge>}
                  </div>
                  {p.description && (
                    <div className="text-xs text-surface-muted mt-0.5 max-w-md">
                      {p.description}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {SUMMARY_TAGS.filter(t => p[t.key]).map(t => (
                      <Badge key={t.key} tone="green">{t.short}</Badge>
                    ))}
                    {!SUMMARY_TAGS.some(t => p[t.key]) && (
                      <span className="text-xs text-surface-muted">— none —</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-xs">
                  {p.allowed_countries.length > 0
                    ? p.allowed_countries.map(c => `+${c}`).join(', ')
                    : '—'}
                </td>
                <td className="px-3 py-2 text-xs">
                  {p.blocked_countries.length > 0
                    ? p.blocked_countries.map(c => `+${c}`).join(', ')
                    : '—'}
                </td>
                <td className="px-3 py-2 text-xs">{p.client_name || '—'}</td>
                <td className="px-3 py-2 text-right">
                  <Badge tone={p.assignee_count > 0 ? 'blue' : 'gray'}>
                    {p.assignee_count}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" onClick={() => setEditing(p)}>
                      Edit
                    </Button>
                    <Button variant="ghost"
                      onClick={() => onDelete(p)}
                      disabled={p.is_default_block || delMut.isPending}
                      title={p.is_default_block ? 'System profile - cannot delete' : ''}>
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <ProfileEditModal
          profile={editing === 'new' ? null : editing}
          clients={clients}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['dialing-profiles'] });
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Profile edit modal
// ---------------------------------------------------------------------

function ProfileEditModal({ profile, clients, onClose, onSaved }) {
  const isNew = !profile;
  const isLocked = !!profile?.is_default_block;
  const [form, setForm] = useState({
    name:        profile?.name        || '',
    description: profile?.description || '',
    client_id:   profile?.client_id   || '',
    allow_domestic_us: profile?.allow_domestic_us ?? true,
    allow_canada:      profile?.allow_canada      ?? false,
    allow_mexico:      profile?.allow_mexico      ?? false,
    allow_intl_other:  profile?.allow_intl_other  ?? false,
    allowed_countries: (profile?.allowed_countries || []).join(', '),
    blocked_countries: (profile?.blocked_countries || []).join(', '),
  });
  const [err, setErr] = useState(null);

  const saveMut = useMutation({
    mutationFn: async (payload) =>
      isNew
        ? dialingProfilesApi.create(payload)
        : dialingProfilesApi.update(profile.id, payload),
    onSuccess: onSaved,
    onError: (e) => setErr(String(e.detail || e.message || 'Save failed')),
  });

  const parseCountries = (csv) =>
    (csv || '')
      .split(/[\s,;]+/)
      .map(s => s.trim().replace(/^\+/, ''))
      .filter(Boolean);

  const onSubmit = (e) => {
    e.preventDefault();
    setErr(null);
    if (!form.name.trim()) {
      setErr('Name is required.');
      return;
    }
    const payload = {
      name: form.name.trim(),
      description: form.description || null,
      client_id: form.client_id || null,
      allow_domestic_us: !!form.allow_domestic_us,
      allow_canada:      !!form.allow_canada,
      allow_mexico:      !!form.allow_mexico,
      allow_intl_other:  !!form.allow_intl_other,
      allowed_countries: parseCountries(form.allowed_countries),
      blocked_countries: parseCountries(form.blocked_countries),
    };
    if (isLocked) {
      // Default Block accepts description only.
      saveMut.mutate({ description: payload.description });
    } else {
      saveMut.mutate(payload);
    }
  };

  return (
    <Modal title={isNew ? 'New dialing profile' : `Edit profile: ${profile.name}`} onClose={onClose} size="lg">
      <form onSubmit={onSubmit} className="space-y-4">
        {err && <ErrorBanner>{err}</ErrorBanner>}
        {isLocked && (
          <div className="px-3 py-2 rounded-thin bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-sm">
            ⚠️ This is the system <strong>Default Block</strong> profile.
            Only the description is editable.
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Name</Label>
            <Input
              value={form.name}
              disabled={isLocked}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. NYC - US Only"
              required
            />
          </div>
          <div>
            <Label>Client (optional tag)</Label>
            <select
              value={form.client_id}
              disabled={isLocked}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              className="w-full px-3 py-2 rounded-thin border border-surface-border dark:border-surface-dark-border bg-white dark:bg-surface-dark-panel"
            >
              <option value="">— No client tag —</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <Label>Description</Label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
            className="w-full px-3 py-2 rounded-thin border border-surface-border dark:border-surface-dark-border bg-white dark:bg-surface-dark-panel"
            placeholder="What is this profile for?"
          />
        </div>

        <fieldset className="border border-surface-border dark:border-surface-dark-border rounded-thin p-3">
          <legend className="text-xs px-2 text-surface-muted">Allowed regions</legend>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <Toggle disabled={isLocked} checked={form.allow_domestic_us}
              onChange={v => setForm({ ...form, allow_domestic_us: v })}
              label="Domestic US (NANP)"
              note="+1 to US NPAs" />
            <Toggle disabled={isLocked} checked={form.allow_canada}
              onChange={v => setForm({ ...form, allow_canada: v })}
              label="Canada"
              note="+1 to Canadian NPAs" />
            <Toggle disabled={isLocked} checked={form.allow_mexico}
              onChange={v => setForm({ ...form, allow_mexico: v })}
              label="Mexico"
              note="+52..." />
            <Toggle disabled={isLocked} checked={form.allow_intl_other}
              onChange={v => setForm({ ...form, allow_intl_other: v })}
              label="Other international"
              note="Any other +CC..." />
          </div>
        </fieldset>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Explicit ALLOW country codes</Label>
            <Input
              value={form.allowed_countries}
              disabled={isLocked}
              onChange={(e) => setForm({ ...form, allowed_countries: e.target.value })}
              placeholder="e.g. 44, 49, 81"
            />
            <div className="text-xs text-surface-muted mt-1">
              Comma-separated country codes (no leading +). These are allowed
              IN ADDITION TO the booleans above.
            </div>
          </div>
          <div>
            <Label>Explicit BLOCK country codes</Label>
            <Input
              value={form.blocked_countries}
              disabled={isLocked}
              onChange={(e) => setForm({ ...form, blocked_countries: e.target.value })}
              placeholder="e.g. 234"
            />
            <div className="text-xs text-surface-muted mt-1">
              Hard block. Wins over everything else.
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={saveMut.isPending}>
            {saveMut.isPending ? 'Saving…' : 'Save profile'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function Toggle({ checked, onChange, label, note, disabled }) {
  return (
    <label className={`flex items-start gap-2 p-2 rounded-thin border ${
      checked ? 'border-continuum-300 bg-continuum-50 dark:border-continuum-700 dark:bg-continuum-900/30'
              : 'border-surface-border dark:border-surface-dark-border'
    } ${disabled ? 'opacity-60' : 'cursor-pointer'}`}>
      <input
        type="checkbox"
        className="mt-1"
        disabled={disabled}
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <div>
        <div className="font-medium text-sm">{label}</div>
        {note && <div className="text-xs text-surface-muted">{note}</div>}
      </div>
    </label>
  );
}
